import { Alert, Button, Loader, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ChosenInvoice, InventoryInvoices } from 'src/Components/Inventory/InventoryInvoices';
import {
  InventoryFormValues,
  InventoryItemFields,
} from 'src/Components/Inventory/InventoryItemFields';
import { InventoryPhotoPicker, PickedPhoto } from 'src/Components/Inventory/InventoryPhotoPicker';
import { useInventoryRooms } from 'src/Components/Inventory/useInventoryRooms';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useUpdateInventoryItem } from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory, { useFetchInventoryItem } from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import styles from './InventoryEditPage.module.scss';

/** Edit an object: same card as after Lucien, with its photos and invoices. */
const InventoryEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { itemId } = useParams<{ itemId: string }>();
  const { data: item, isLoading } = useFetchInventoryItem(itemId);
  const { data: inventory } = useFetchInventory();
  const { roomOptions } = useInventoryRooms();
  const { mutate: update, isLoading: isSaving } = useUpdateInventoryItem(itemId ?? '');

  const [values, setValues] = useState<InventoryFormValues | null>(null);
  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  const [invoices, setInvoices] = useState<ChosenInvoice[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!item || values) return;
    setValues({
      name: item.name,
      roomId: item.room?.id ?? null,
      brandAndModel: item.brandAndModel ?? '',
      condition: item.condition ?? '',
      notes: item.notes ?? '',
      quantity: item.quantity,
      purchaseYear: item.purchaseYear ?? null,
      purchasePrice: item.purchasePrice ?? null,
      replacementValue: item.replacementValue ?? null,
      replacementValueSource: item.replacementValueSource ?? null,
      replacementValueSourceLabel: item.replacementValueSourceLabel ?? null,
      replacementValueSourceUrl: item.replacementValueSourceUrl ?? null,
      depreciationEnabled: item.depreciationEnabled,
    });
    setPhotos(item.photos.map(photo => ({ key: photo.id, serverFile: photo })));
    setInvoices(
      item.invoices.map(invoice => ({ id: invoice.id, name: invoice.name, url: invoice.url }))
    );
  }, [item, values]);

  const onPhotosChange = useCallback(
    (change: (current: PickedPhoto[]) => PickedPhoto[]) => setPhotos(change),
    []
  );

  if (isLoading || !values) return <Loader />;
  if (!item) return <Text>{t('inventory.item.error', 'Impossible de retrouver cet objet.')}</Text>;

  const itemPath = `/asset/${assetId}/inventories/${item.id}`;
  const valuesVisible = item.valuesVisible;

  const save = () => {
    setError(null);
    if (!values.name.trim()) {
      setError(t('inventory.add.nameRequired', "Donnez un nom à l'objet (ex. « Canapé »)."));
      return;
    }
    if (!photos.length) {
      setError(t('inventory.add.photoRequired', 'Prenez au moins une photo de l’objet.'));
      return;
    }
    if (photos.some(photo => !photo.serverFile)) {
      setError(t('inventory.add.photosPending', 'Attendez que les photos soient envoyées.'));
      return;
    }
    update(
      {
        name: values.name.trim(),
        roomId: values.roomId,
        brandAndModel: values.brandAndModel || null,
        condition: values.condition || null,
        notes: values.notes || null,
        quantity: values.quantity,
        purchaseYear: values.purchaseYear,
        depreciationEnabled: values.depreciationEnabled,
        ...(valuesVisible && {
          purchasePrice: values.purchasePrice,
          replacementValue: values.replacementValue,
          replacementValueSource: values.replacementValueSource,
          replacementValueSourceLabel: values.replacementValueSourceLabel,
          replacementValueSourceUrl: values.replacementValueSourceUrl,
        }),
        photoIds: photos.map(photo => photo.serverFile!.id),
        invoiceFileIds: invoices.map(invoice => invoice.id),
      },
      {
        onSuccess: () => {
          notifications.show({
            color: 'teal',
            message: t('inventory.edit.saved', 'Fiche enregistrée.'),
          });
          navigate(itemPath);
        },
        onError: () =>
          setError(t('inventory.edit.error', "La fiche n'a pas pu être enregistrée. Réessayez.")),
      }
    );
  };

  return (
    <div className={styles.container}>
      <Link to={itemPath}>
        <Button variant="transparent" leftSection={<IconArrowLeft size={18} />}>
          {t('common.back', 'Retour')}
        </Button>
      </Link>
      <PageHeader label={t('inventory.edit.title', 'Modifier {{name}}', { name: item.name })} />

      <InventoryPhotoPicker photos={photos} onChange={onPhotosChange} />
      <InventoryItemFields
        values={values}
        onChange={patch => setValues(current => (current ? { ...current, ...patch } : current))}
        roomOptions={roomOptions}
        valuesVisible={valuesVisible}
        rule={inventory?.settings ?? { depreciationRatePercent: 10, depreciationMaxPercent: 80 }}
      />
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>{t('inventory.add.invoices', 'Factures')}</Text>
        <InventoryInvoices
          invoices={invoices}
          onAdd={invoice => setInvoices(current => [...current, invoice])}
          onRemove={id => setInvoices(current => current.filter(invoice => invoice.id !== id))}
        />
      </div>

      {error && <Alert color="red">{error}</Alert>}
      <div className={styles.actions}>
        <Button variant="outline" size="md" onClick={() => navigate(itemPath)}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button size="md" loading={isSaving} onClick={save}>
          {t('common.save', 'Enregistrer')}
        </Button>
      </div>
    </div>
  );
};

export default InventoryEditPage;
